import { Target, Brain, Zap, Shield, Lightbulb } from "lucide-react";
import { P, getScoreColor, getScoreLabel, ScoreArc, SectionTitle, KPICard } from "./report-utils";

export function SnapshotTab({ total, archetype, aps, defense, consistency, interpretation }) {
    return (
        <div data-testid="section-snapshot">
            <SectionTitle icon={Target} title="Executive Snapshot" subtitle="At-a-glance coachability overview" id="snapshot" />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col items-center text-center shadow-sm col-span-2 lg:col-span-1">
                    <ScoreArc score={total} label="Coachability Score" sub={`of 100 | ${getScoreLabel(total)}`} />
                </div>
                <KPICard icon={Brain} value={archetype?.name || "N/A"} label="Archetype"
                    sub={archetype?.score_range} color={P.blue} bg="#EFF6FF" />
                <KPICard icon={Zap} value={`${(aps?.score || 0).toFixed(0)}`} label="Action Probability"
                    sub={aps?.roi || ""} color={P.green} bg={P.greenBg} />
                <KPICard icon={Shield}
                    value={`${defense?.count || 0}`} label="Defense Index"
                    sub={`${defense?.label || ""}`}
                    color={defense?.count >= 8 ? P.red : defense?.count >= 4 ? P.amber : P.green}
                    bg={defense?.count >= 8 ? P.redBg : defense?.count >= 4 ? P.amberBg : P.greenBg} />
            </div>

            <div className="rounded-xl border border-slate-100 bg-white p-5 sm:p-6 mb-6 shadow-sm">
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Lightbulb className="h-4 w-4 text-blue-500" />
                    </div>
                    <div>
                        <p className="text-[0.65rem] font-bold uppercase tracking-[0.15em] text-slate-400 mb-1">Key Insight</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{interpretation}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <p className="text-[0.6rem] font-bold uppercase tracking-widest text-slate-400 mb-2">Consistency Index</p>
                    <div className="flex items-end gap-2">
                        <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{consistency?.score || 0}</span>
                        <span className="text-xs text-slate-400 mb-1">/ 100</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                        <div className="h-1.5 rounded-full" style={{ width: `${consistency?.score || 0}%`, background: getScoreColor(consistency?.score || 0), transition: "width 1s" }} />
                    </div>
                    <p className="text-[0.65rem] text-slate-400 mt-2">{consistency?.drops || 0} behavioral drops detected</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <p className="text-[0.6rem] font-bold uppercase tracking-widest text-slate-400 mb-2">Defense Pattern</p>
                    <div className="flex items-end gap-2">
                        <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{defense?.level || "N/A"}</span>
                    </div>
                    <p className="text-xs font-medium mt-2" style={{ color: defense?.count >= 8 ? P.red : defense?.count >= 4 ? P.amber : P.green }}>
                        {defense?.label}
                    </p>
                    <p className="text-[0.65rem] text-slate-400 mt-1">{defense?.count} defensive responses</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <p className="text-[0.6rem] font-bold uppercase tracking-widest text-slate-400 mb-2">Action Probability</p>
                    <div className="flex items-end gap-2">
                        <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{(aps?.score || 0).toFixed(1)}</span>
                        <span className="text-xs text-slate-400 mb-1">/ 100</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                        <div className="h-1.5 rounded-full" style={{ width: `${aps?.score || 0}%`, background: getScoreColor(aps?.score || 0), transition: "width 1s" }} />
                    </div>
                    <p className="text-[0.65rem] font-medium mt-2" style={{ color: getScoreColor(aps?.score || 0) }}>{aps?.roi}</p>
                </div>
            </div>
        </div>
    );
}
